import { Alert, AlertTitle, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import { ApiError } from '../../@types';
import { ResolveOpenBettingSlipsResponse } from '../../@types/api';
import API_ROUTES from '../../constants/apiRoutes';
import { useModal } from '../../contexts/ModalContext';
import { useToast } from '../../contexts/ToastContext';
import { useToastErrorMessage } from '../../hooks/useToastErrorMessage';
import { postData } from '../../utils/api';
import ConfirmationModal from '../modals/ConfirmationModal';

const styles = {
  page: {
    width: '100%',
    maxWidth: 960,
  },
  card: {
    p: { xs: 2, md: 3 },
    borderRadius: 2,
  },
  section: {
    gap: 3,
  },
  actionItem: {
    gap: 2,
    '&:not(:last-of-type)': {
      pb: 3,
      borderBottom: '1px solid',
      borderColor: 'divider',
    },
  },
  cardLayout: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
    gap: { xs: 2, md: 3 },
    alignItems: { xs: 'flex-start', md: 'center' },
  },
  content: {
    minWidth: 0,
  },
  title: {
    fontWeight: 600,
  },
  actionButton: {
    minWidth: 112,
    justifySelf: { md: 'flex-end' },
  },
  resultChips: {
    flexWrap: 'wrap',
  },
};

const SuperAdminActionsPage = () => {
  const { openModal, closeModal } = useModal();
  const { enqueueSnackbar } = useToast();
  const { handleErrorMessage } = useToastErrorMessage();
  const resolveOpenBettingSlipsMutation = useMutation<ResolveOpenBettingSlipsResponse, ApiError, void>({
    mutationFn: () => {
      return postData(API_ROUTES.SUPER_ADMIN_RESOLVE_OPEN_BETTING_SLIPS, {}, 'ftbConsumer');
    },
    onSuccess: () => {
      enqueueSnackbar({ message: 'Open betting slip resolution finished successfully.', variant: 'success' });
    },
    onError: (error) => {
      handleErrorMessage(error);
    },
  });

  const openResolveConfirmationModal = () => {
    openModal(
      <ConfirmationModal
        title='Run open betting slip resolution now?'
        actionButtonLabel='Confirm'
        actionButtonClose='Cancel'
        handleConfirm={() => resolveOpenBettingSlipsMutation.mutate()}
        closeModal={closeModal}
      />
    );
  };

  return (
    <Stack sx={styles.page}>
      <Paper elevation={0} sx={styles.card}>
        <Stack sx={styles.section}>
          <Stack sx={styles.actionItem}>
            <Stack sx={styles.cardLayout}>
              <Stack spacing={0.75} sx={styles.content}>
                <Typography variant='h6' sx={styles.title}>
                  Resolve Open Betting Slips
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Re-run the backend resolution flow for slips that are still open and need another pass.
                </Typography>
              </Stack>

              <Button
                variant='contained'
                onClick={openResolveConfirmationModal}
                disabled={resolveOpenBettingSlipsMutation.isPending}
                sx={styles.actionButton}
              >
                Run
              </Button>
            </Stack>

            {resolveOpenBettingSlipsMutation.data && (
              <Alert severity='success'>
                <AlertTitle>Last run summary</AlertTitle>
                <Stack direction='row' spacing={1} useFlexGap sx={styles.resultChips}>
                  <Chip
                    size='small'
                    label={`Open slips: ${resolveOpenBettingSlipsMutation.data.openBettingSlipsCount}`}
                  />
                  <Chip
                    size='small'
                    label={`Resolved: ${resolveOpenBettingSlipsMutation.data.resolvedBettingSlipsCount}`}
                  />
                  <Chip size='small' label={`Updated bets: ${resolveOpenBettingSlipsMutation.data.updatedBetsCount}`} />
                  <Chip
                    size='small'
                    label={`Failed: ${resolveOpenBettingSlipsMutation.data.failedBettingSlipsCount}`}
                  />
                </Stack>
              </Alert>
            )}
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default SuperAdminActionsPage;
